// The one project list for every module (30-day list row 18). A bid and a project are the
// same record in two states: before award it is a bid, after award it is a project. One
// query, one cache, one remembered choice that follows the person from module to module,
// one picker that lists projects first and bids after.
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { AWARDED_LIFECYCLE, ACTIVE_BID_STAGES } from '@tradeview/shared';
import { db } from '../firebase';
import { useAuth } from '../auth';

/** Every field a module reads off the project document. Add, never rename. */
export interface Project {
  id: string; name: string; stage: string; bid: boolean;
  address: string | null; owner: string | null; customerName: string | null; jobType: string | null;
  pm: string | null; foreman: string | null; team: string[];
  gcPmName: string | null; gcPmEmail: string | null;
  value: number; coApprovedTotal: number;
  startDate: string | null; targetEnd: string | null;
  billingCutoffDay: number | null; sol: string | null; mailKey: string | null;
  rateTable: string | null; estManHours: number | null; pctComplete: number | null;
  plansLink: string | null; lightingLink: string | null; gearLink: string | null;
}
const str = (v: unknown) => (typeof v === 'string' && v) ? v : null;
const num = (v: unknown) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const numOrNull = (v: unknown) => v == null || v === '' ? null : Number.isFinite(Number(v)) ? Number(v) : null;
export function projectOf(id: string, x: Record<string, unknown>): Project {
  const stage = String(x.stage || '');
  return {
    id, name: String(x.name || ''), stage, bid: !AWARDED_LIFECYCLE.includes(stage as never),
    address: str(x.address), owner: str(x.owner), customerName: str(x.customerName), jobType: str(x.jobType),
    pm: str(x.pm), foreman: str(x.foreman), team: Array.isArray(x.team) ? (x.team as string[]) : [],
    gcPmName: str(x.gcPmName), gcPmEmail: str(x.gcPmEmail),
    value: num(x.value), coApprovedTotal: num(x.coApprovedTotal),
    startDate: str(x.startDate), targetEnd: str(x.targetEnd),
    billingCutoffDay: numOrNull(x.billingCutoffDay), sol: str(x.sol), mailKey: str(x.mailKey),
    rateTable: str(x.rateTable), estManHours: numOrNull(x.estManHours), pctComplete: numOrNull(x.pctComplete),
    plansLink: str(x.plansLink), lightingLink: str(x.lightingLink), gearLink: str(x.gearLink),
  };
}
/** Projects first, then bids, each group by name. */
export const sortProjects = (a: Project, b: Project) => Number(a.bid) - Number(b.bid) || a.name.localeCompare(b.name);

export interface ProjectsOptions {
  /** Include the open bids (identification → submitted, rebid). Default: awarded projects only. */
  bids?: boolean;
  /** Every project document regardless of stage (lost, closed, …). Rarely right. */
  all?: boolean;
}
export const PROJECTS_KEY = 'projects';
/** The company's projects (and bids when asked), one cached query shared by every module. */
export function useProjects(opts: ProjectsOptions = {}) {
  const { session } = useAuth();
  if (!session) throw new Error('signed out');
  const companyId = session.paths.companyId;
  const all = !!opts.all;
  const q = useQuery({
    queryKey: [PROJECTS_KEY, companyId, all ? 'all' : 'open'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const col = collection(db, session.paths.projects);
      const snap = await getDocs(all ? col : query(col, where('stage', 'in', [...AWARDED_LIFECYCLE, ...ACTIVE_BID_STAGES])));
      return snap.docs.filter(d => !d.data().archived).map(d => projectOf(d.id, d.data())).sort(sortProjects);
    },
  });
  const wantBids = !!opts.bids || all;
  const data = useMemo(() => q.data ? (wantBids ? q.data : q.data.filter(p => !p.bid)) : undefined, [q.data, wantBids]);
  return { ...q, data } as typeof q;
}

// ── the remembered choice: one key for the whole app (older per-module keys still read) ──
const LS_KEY = 'tv.project';
const LEGACY_KEYS = ['tv.plan.job', 'tv.controls.job', 'tv.procurement.job', 'tv.billing.job', 'tv.costing.job', 'tv.mail.job', 'tv.drawings.job', 'tv.punch.job', 'tv.records.job', 'tv.reports.job', 'tv.materials.job', 'tv.channel.job', 'tv.clock.job'];
export function rememberProject(id: string) { try { if (id) { localStorage.setItem(LS_KEY, id); window.dispatchEvent(new Event('tv:project')); } } catch { /* fine */ } }
export function rememberedProject(): string {
  try { const v = localStorage.getItem(LS_KEY); if (v) return v; for (const k of LEGACY_KEYS) { const x = localStorage.getItem(k); if (x) return x; } } catch { /* fine */ }
  return '';
}

/**
 * The current project for a screen: the URL's choice, else the remembered one, else the first
 * on the list. A remembered id that is not on this module's list (a bid on an awarded-only
 * module, an archived project) falls back to the first once the list is in.
 */
export function useProject(pidParam: string | null | undefined, opts: ProjectsOptions = {}) {
  const projects = useProjects(opts);
  const [chosen, setChosen] = useState(pidParam || rememberedProject());
  const list = projects.data ?? [];
  const pid = projects.data ? (chosen && list.some(p => p.id === chosen) ? chosen : (list[0]?.id ?? '')) : chosen;
  const project = list.find(p => p.id === pid) ?? null;
  // Arriving by a link that names the project (Today's rows, a push, the front door) is entering that room: the shell follows.
  useEffect(() => { if (pidParam && list.some(p => p.id === pidParam) && rememberedProject() !== pidParam) rememberProject(pidParam); }, [pidParam, list]);
  const setPid = (id: string) => { setChosen(id); rememberProject(id); };
  return { pid, project, projects: list, setPid, loading: projects.isLoading };
}

/** The picker: projects first, bids under their own heading. Since the rooms (30-day rows 30 and 31) the room picks the
 *  project, so a page's picker draws the project's name instead of a select; only the room's own band (and a settings
 *  page that truly chooses a job) passes `choose` to get the select. */
export type PickableProject = Pick<Project, 'id' | 'name'> & { bid?: boolean };
export function ProjectPicker({ value, onChange, projects, id, className = 'field-in min-h-9', blank, disabled, choose }: { value: string; onChange: (id: string) => void; projects: PickableProject[]; id?: string; className?: string; blank?: string; disabled?: boolean; choose?: boolean }) {
  const jobs = projects.filter(p => !p.bid), bids = projects.filter(p => p.bid);
  if (!choose) {
    const p = projects.find(x => x.id === value);
    return p ? <div className="text-[12.5px] text-muted truncate" aria-label="Project">{p.bid ? 'Bid' : 'Job'} · {p.name}</div> : null;
  }
  return (
    <select id={id} className={className} value={value} onChange={e => onChange(e.target.value)} aria-label="Project" disabled={disabled}>
      {blank !== undefined && <option value="">{blank}</option>}
      {bids.length && jobs.length ? <optgroup label="Projects">{jobs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup> : jobs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      {bids.length > 0 && <optgroup label="Bids">{bids.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>}
    </select>
  );
}
